import { Injectable } from '@angular/core';
import { CssUtils } from '@common/flare-data/util/css-utils';
import { CssItem, CssMedium, CssProperty, CssRoot, CssRule, CssStylesheet } from '@portal-core/project-files/stylesheets/models/css-stylesheet.model';
import { AtRule, Declaration, Root, Rule } from 'postcss';

@Injectable({
  providedIn: 'root'
})
export class CssStylesheetService {

  static readonly Mediums: Dictionary<string> = {
    '': undefined,
    'tablet': '',
    'mobile': 'tablet',
    'print': ''
  }

  /** Loads the CSS stylesheet from the specified content. */
  public load(path: string, content: Root | string): CssStylesheet {
    const rootNode: Root = typeof content === 'string'
      ? CssUtils.parse(content)
      : content;
    const root = this.parseRoot(rootNode);

    const stylesheet = new CssStylesheet();
    stylesheet.path = path;
    stylesheet.isFactory = (path + '').startsWith('%assets%');
    this.setRoot(stylesheet, root);
    return stylesheet;
  }

  /** Sets the root for the specified stylesheet. */
  private setRoot(stylesheet: CssStylesheet, root: CssRoot): void {
    root.parent = stylesheet;
    stylesheet.root = root;
  }

  /** Parses the AST root node into CssRoot structure. */
  private parseRoot(node: Root): CssRoot {
    const root = new CssRoot();
    root.node = node;

    node.nodes.forEach(node => {
      if (node.type === 'rule') {
        const rules = this.parseSimpleRules(node);
        rules.forEach(rule => this.addRule(root, rule));
      } else if (node.type === 'atrule' && node.name === 'media') {
        const cssMedium = this.parseMedium(node);
        this.addMedium(root, cssMedium)
      }
    });

    return root;
  }

  /** Parses a media at-rule into CssMedium structure. */
  private parseMedium(node: AtRule): CssMedium {
    const medium = new CssMedium();
    medium.node = node;

    node.nodes.forEach(node => {
      if (node.type === 'rule') {
        const rules = this.parseSimpleRules(node);
        rules.forEach(rule => this.addRule(medium, rule));
      }
    });

    return medium;
  }

  /** Adds a medium to the specified root, merging if already exists. */
  private addMedium(parent: CssRoot, medium: CssMedium): void {
    medium.parent = parent;
    // try to find the existing medium with same params
    const existingMedium = this.lookupMedium(parent, medium.params);
    if (existingMedium) {
      // merge the added medium into the existing one
      this.mergeMediums(existingMedium, medium);
      // remove the existing medium from the list to add it later
      const idx = parent.items.indexOf(existingMedium);
      parent.items.splice(idx, 1);
      medium = existingMedium;
    }
    // add the (new or merged) medium to the end of the list
    parent.items.push(medium);
  }

  /** Looks up a medium by its params in the specified root. */
  private lookupMedium(parent: CssRoot, params: string): CssMedium | null {
    for (const item of parent.items) {
      if (item instanceof CssMedium && item.params.toLowerCase() === params.toLowerCase())
        return item;
    }
    return null;
  }

  /** Merges two mediums, with target medium overriding source medium rules. */
  private mergeMediums(target: CssMedium, source: CssMedium): void {
    target.node = source.node; // keep the latest definition
    source.items.forEach(item => {
      if (item instanceof CssRule)
        this.addRule(target, item);
    });
  }

  /** Returns an array of of parsed css rules: one css rule for each rule's selector. */
  private parseSimpleRules(node: Rule): CssRule[] {
    const rules: CssRule[] = [];
    for (const selector of node.selectors) {
      const rule = new CssRule();
      rule.node = node;
      rule.selector = selector;
      rules.push(rule);
    }

    node.nodes.forEach(node => {
      if (node.type === 'decl') {
        rules.forEach(cssRule => {
          const property = this.parseProperty(node);
          this.addProperty(cssRule, property);
        });
      }
    });

    return rules;
  }

  /** Adds a rule to the specified parent, merging if already exists. */
  private addRule(parent: CssRoot | CssMedium, rule: CssRule): void {
    rule.parent = parent;
    const existingRule = this.lookupRule(parent, rule.selector);
    if (existingRule) {
      this.mergeRules(existingRule, rule);
      const idx = parent.items.indexOf(existingRule);
      parent.items.splice(idx, 1);
      rule = existingRule;
    }
    parent.items.push(rule);
  }

  /** Looks up a rule by its selector in the specified parent. */
  private lookupRule(parent: CssRoot | CssMedium, selector: string): CssRule | null {
    for (const item of parent.items) {
      if (item instanceof CssRule && item.selector.toLowerCase() === selector.toLowerCase())
        return item;
    }
    return null;
  }

  /** Merges two rules, with target rule overriding source rule properties. */
  private mergeRules(target: CssRule, source: CssRule): void {
    target.node = source.node; // keep the latest definition
    source.items.forEach(item => {
      if (item instanceof CssProperty)
        this.addProperty(target, item);
    });
  }

  /** Parses a declaration node into CssProperty structure. */
  private parseProperty(node: Declaration): CssProperty {
    const property = new CssProperty();
    property.node = node;
    return property;
  }

  /** Adds a property to the specified rule, merging if already exists. */
  private addProperty(parent: CssRule, property: CssProperty): void {
    const existingProperty = this.lookupProperty(parent, property.name);
    // if the existing css property has an `important` specifier and the adding property has no one
    if (existingProperty?.important && !property.important) {
      return; // keep the existing css property
    }
    // remove the existing css property
    if (existingProperty) {
      const idx = parent.items.indexOf(existingProperty);
      parent.items.splice(idx, 1);
    }
    // add the new property
    property.parent = parent;
    parent.items.push(property);
  }

  /** Looks up a property by its name in the specified rule. */
  private lookupProperty(rule: CssRule, propertyName: string): CssProperty | null {
    for (const item of rule.items) {
      if (item instanceof CssProperty && item.name === propertyName)
        return item;
    }
    return null;
  }

  /** Gets the stylesheet of the specified `cssItem`. */
  public getStylesheet(cssItem: CssItem): CssStylesheet {
    while (cssItem) {
      if (cssItem instanceof CssStylesheet) {
        return cssItem;
      }
      cssItem = cssItem.parent;
    }
  }

  /** Gets all stylesheet rules for the specified medium. */
  public getRules(stylesheet: CssStylesheet, medium: string = null): CssRule[] {
    const rules = [];
    const mediums = CssUtils.getMediums(medium);
    if (stylesheet?.root) {
      stylesheet.root.items.forEach(item => {
        if (item instanceof CssRule)
          rules.push(item);
        else if (item instanceof CssMedium && mediums.includes(item.params))
          item.items.forEach(item => {
            if (item instanceof CssRule)
              rules.push(item);
          });
      });
    }
    return rules;
  }
}
